import {AnalyticsService} from 'src/analytics';
import type {SchedulerTemplateForm} from 'src/types/scheduler/template-form';
import type {Template} from 'src/types/scheduler/templates';

import {SCHEDULER_AMPLITUDE_EVENTS} from './scheduler-amplitude-constants';
import {
  buildTemplateCreatedAmplitudeProps,
  getSchedulerAmplitudeTemplateTypeLabel,
  getTemplateUpdatedFieldsChanged,
  templatesPageLabel,
} from './scheduler-amplitude-helper';
import {getTemplateFormAnalyticsState} from './template-form-analytics-state';


export function trackIndividualTemplatesPageVisited(
  agency_id: string,
  agency_name: string,
): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.TEMPLATE.TEMPLATES_PAGE_VISITED,
    {agency_id, agency_name},
  );
}

export function trackTeamTemplatesPageVisited(
  agency_id: string,
  agency_name: string,
): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.TEMPLATE.TEAM_TEMPLATES_PAGE_VISITED,
    {agency_id, agency_name},
  );
}

export function trackTemplateCreationCancelled(
  agency_id: string,
  agency_name: string,
  isTeamTemplate: boolean,
): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.TEMPLATE.TEMPLATE_CREATION_CANCELLED,
    {
      agency_id,
      agency_name,
      page: templatesPageLabel(isTeamTemplate),
    },
  );
}

export function trackCopyLinkClicked({
  agency_id,
  agency_name,
  isTeamTemplate,
  urlType,
  distributionRule,
}: {
  agency_id: string;
  agency_name: string;
  isTeamTemplate: boolean;
  urlType: string;
  distributionRule: string;
}): void {
  AnalyticsService.track(
    SCHEDULER_AMPLITUDE_EVENTS.TEMPLATE.COPY_LINK_CLICKED,
    {
      agency_id,
      agency_name,
      template_type: getSchedulerAmplitudeTemplateTypeLabel(distributionRule),
      page: templatesPageLabel(isTeamTemplate),
      url_type: urlType,
    },
  );
}

export function trackTemplateCreated({
  agency_id,
  agency_name,
  template,
  isTeamTemplate,
}: {
  agency_id: string;
  agency_name: string;
  template: Template;
  isTeamTemplate: boolean;
}): void {
  AnalyticsService.track(SCHEDULER_AMPLITUDE_EVENTS.TEMPLATE.TEMPLATE_CREATED, {
    agency_id,
    agency_name,
    page: templatesPageLabel(isTeamTemplate),
    ...buildTemplateCreatedAmplitudeProps(template as SchedulerTemplateForm),
  });
}

export function trackTemplateDeleted({
  agency_id,
  agency_name,
  distributionRule,
  pageIsTeamTemplate,
}: {
  agency_id: string;
  agency_name: string;
  distributionRule: string;
  pageIsTeamTemplate: boolean;
}): void {
  AnalyticsService.track(SCHEDULER_AMPLITUDE_EVENTS.TEMPLATE.TEMPLATE_DELETED, {
    agency_id,
    agency_name,
    template_type: getSchedulerAmplitudeTemplateTypeLabel(distributionRule),
    page: templatesPageLabel(pageIsTeamTemplate),
  });
}

export function trackTemplateSaveSuccess({
  templateFormBeforeOverwrite,
  agency,
}: {
  templateFormBeforeOverwrite: SchedulerTemplateForm;
  agency: {id: string; name: string};
}): void {
  const baseline = getTemplateFormAnalyticsState();
  if (baseline == null) {
    return;
  }
  const fieldsChanged = getTemplateUpdatedFieldsChanged(
    baseline,
    templateFormBeforeOverwrite,
  );
  if (fieldsChanged === '') {
    return;
  }

  AnalyticsService.track(SCHEDULER_AMPLITUDE_EVENTS.TEMPLATE.TEMPLATE_UPDATED, {
    agency_id: agency.id,
    agency_name: agency.name,
    template_type: getSchedulerAmplitudeTemplateTypeLabel(
      templateFormBeforeOverwrite.distributionRule,
    ),
    fields_changed: fieldsChanged,
  });
}
